import { memo } from 'react';

import { type ListSlackIntegrationsObject } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';

import { ConnectedToSlackToggles } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/components/ConnectedToSlackToggles';
import { type UpdateBoardSlackIntegrationProps } from '~/swr-hooks/slack/useUpdateBoardSlackIntegration';
import { ConnectedSlackChannel } from './ConnectedSlackChannel';
import { DeleteBoardSlackIntegrationModal } from './DeleteBoardSlackIntegrationModal';

type ConnectedToSlackProps = {
  integration: ListSlackIntegrationsObject;
  boardId: string;
  canManageSlackIntegration?: boolean;
  onUpdate: (params: UpdateBoardSlackIntegrationProps) => void;
};

export const ConnectedToSlack = memo(
  ({ integration, boardId, canManageSlackIntegration, onUpdate }: ConnectedToSlackProps) => {
    const [showDeleteSlackIntegrationModal] = useAirModal(DeleteBoardSlackIntegrationModal);

    return (
      <div className="group/connected-slack-container mt-16 rounded-6 border border-grey-3 bg-grey-3 px-3 py-2 pb-4">
        <div className="flex items-center justify-between">
          <ConnectedSlackChannel channelName={integration.channelName} integration={integration} />
          {canManageSlackIntegration && (
            <Tooltip
              side="bottom"
              label={
                integration.isCoveredByAncestor && integration.coveredByBoard?.title
                  ? `Connected via ${integration.coveredByBoard.title}`
                  : undefined
              }
            >
              <Button
                appearance="filled"
                disabled={integration.isCoveredByAncestor}
                className="disabled:bg-grey-5 disabled:text-grey-11"
                color="grey"
                onClick={() => showDeleteSlackIntegrationModal({ boardId, integration })}
              >
                Disconnect
              </Button>
            </Tooltip>
          )}
        </div>

        <ConnectedToSlackToggles
          integration={integration}
          onUpdate={onUpdate}
          canManageSlackIntegration={canManageSlackIntegration}
          boardId={boardId}
        />
      </div>
    );
  },
);

ConnectedToSlack.displayName = 'ConnectedToSlack';
